import { User as WpUser } from "../waypoint/User";
import { Coverage } from "./Coverage";
import { CoverageOption } from "./CoverageOption";
import { Driver } from "./Driver";
import { GlobalCertificateHolder } from "./GlobalCertificateHolder";
import { Policy } from "./Policy";
import { PolicyAdditionalInterest } from "./PolicyAdditionalInterest";
import { EquipmentComparison, PolicyLayer } from "./PolicyLayer";
import { Tractor } from "./Tractor";
import { Trailer } from "./Trailer";
import { User as NsUser } from "./User";

export class MonthlyReportPremiumModification {
    policyId: string;
    year: number;
    month: number;
    coverageOptionId: CoverageOption.Id;
    description: string;
    total: number;
}

export class MonthlyReport {
    policyId: string;
    year: number;
    month: number;
    createdDate: Date;
    createdByNorthstarUserGuid: string;
    createdByWaypointUserId: string;
    finalizedDate: Date;
    finalizedByNorthstarUserGuid: string;
    mileage: number;
    revenue: number;
    isFinalized: boolean;
    createdByNorthstarUser: NsUser;
    createdByWaypointUser: WpUser;
    finalizedByNorthstarUser: NsUser;
    submittedDate: Date;
    submittedByWaypointUserId: string;
    isSubmitted: boolean;

    premiumModifications: MonthlyReportPremiumModification[];
    payroll: MonthlyReportPayroll[];

    totalPremiumInCents: number;
}

export class MonthlyReportQueryResult {
    report: MonthlyReport;
    equipment: {
        tractors: Tractor[];
        trailers: Trailer[];
        drivers: Driver[];
    };
    layers: PolicyLayer[];
    totalMileage: number;
    totalRevenue: number;
}

export enum MonthlyReportStatus {
    Overdue,
    Due,
    Submitted,
    Finalized,
    Upcoming,
    NotRequired,
}

export interface MonthlyReportAvailableMonth {
    month: number;
    year: number;
    status: MonthlyReportStatus;
}

export interface MonthlyReportPolicyData {
    policyId: string;
    policyStatus: Policy["status"];
    covereageOptionIds: CoverageOption.Id[];
    writingCompanyName: string;
    description?: string;
    policyNumber: string;
    effectiveDate: string;
    expirationDate: string;
    availableMonths: MonthlyReportAvailableMonth[];
    hasReportDue: boolean;
    reports: MonthlyReport[];
    isAdvancedReporter: boolean;
}

export type MonthlyReportDataUnit<T extends Tractor | Trailer | Driver> = T & {
    isDriver: T extends Driver ? true : false;
    isTractor: T extends Tractor ? true : false;
    isTrailer: T extends Trailer ? true : false;
    changes: Change[];
    isNewToPolicy: boolean;
    dateAddedToPolicy: Date | null;
    isRemovedFromPolicy: boolean;
    dateRemovedFromPolicy: Date | null;
    isNewToCoverage: Map<CoverageOption.Id, boolean>;
    isRemovedFromCoverage: Map<CoverageOption.Id, boolean>;
    isACV: T extends Tractor | Trailer ? boolean : never;
    statedValue: T extends Tractor | Trailer ? number | null : never;
    previousIsACV: T extends Tractor | Trailer ? boolean | null : never;
    previousStatedValue: T extends Tractor | Trailer ? number | null : never;
    experience: T extends Driver ? number : never;
    isSpare: T extends Tractor | Trailer ? boolean : never;
    hasUniqueDeductible: T extends Tractor | Trailer ? boolean : never;
    isOwnerOperator: T extends Driver ? boolean : never;
    /**
     * Drivers, tractors and trailers can each be tied to an employer, so this is no longer narrowed
     * to drivers — declaring it `never` for equipment made the equipment employer unreadable even
     * though the report payload carries it. Optional, matching the underlying entities.
     */
    employer?: GlobalCertificateHolder | null;
    employerId?: number | null;
    additionalInterests: PolicyAdditionalInterest[];
};

export interface MonthlyReportCoveragePremium {
    minimumPremium: number;
    minimumPremiumApplied: number;
    proratedPremiumApplied: number;
    subtotalPremium: number;
    totalPremium: number;
    rates: {
        description: string;
        basis: Coverage["basis"];
        rate: number;
        factor: number;
        total: number;
        prorated: number;
        isModification: boolean;
        /**
         * The units this rate was actually charged on, recorded while the factor is computed so that
         * per-unit cost can be attributed later without re-deriving the eligibility rules. Empty for
         * rates that aren't charged per unit (mileage, revenue, payroll, fees, manual modifications).
         */
        units: MonthlyReportRateUnits;
    }[];
}

export interface MonthlyReportRateUnits {
    tractorIds: number[];
    trailerIds: number[];
    driverIds: number[];
}

/** One rating's contribution to a single unit's cost, kept so the number can be explained. */
export interface MonthlyReportPremiumLine {
    /** the rating's description, e.g. "Tractors" or "" for a coverage rated as a whole */
    description: string;
    basis: Coverage["basis"];
    /** the multiplier applied — already divided by 100 for stated-value ratings */
    rate: number;
    /** this unit's own basis amount: 1 for a per-unit rating, its stated value for a value rating */
    factor: number;
    amount: number;
}

/** What one unit was charged for one coverage on a monthly report. */
export interface MonthlyReportUnitPremium {
    unitId: number;
    unitType: "tractor" | "trailer" | "driver";
    /**
     * Charged directly to this unit by a per-unit or stated-value rate. Exact.
     */
    directPremium: number;
    /**
     * This unit's share of amounts that belong to the coverage but not to any single unit: minimum
     * premium top-ups, manual premium modifications, proration credits, and any mileage/revenue/
     * payroll-rated components. Spread in proportion to `directPremium`, or evenly when no unit on
     * the coverage has a direct charge. This is an allocation, not a fact.
     */
    residualPremium: number;
    /** `directPremium + residualPremium`. These sum to the coverage's `totalPremium`. */
    totalPremium: number;
    /** the ratings that produced `directPremium`, in the order they were applied */
    lines: MonthlyReportPremiumLine[];
}

/** One unit's contribution to a driver's bill for a single coverage. */
export interface MonthlyReportDriverContribution {
    unitType: MonthlyReportUnitPremium["unitType"];
    unitId: number;
    /** how many drivers this unit is assigned to; its cost is divided by this */
    sharedBy: number;
    /** this driver's share, after dividing by `sharedBy` */
    totalPremium: number;
    directPremium: number;
    residualPremium: number;
    lines: MonthlyReportPremiumLine[];
}

/** What one driver owes for one coverage, and which units it came from. */
export interface MonthlyReportDriverCoverageCost {
    totalPremium: number;
    contributions: MonthlyReportDriverContribution[];
}

export interface MonthlyReportCoverageUnitPremiums {
    units: MonthlyReportUnitPremium[];
    /** The coverage total these allocations reconcile to. */
    totalPremium: number;
    /** Total spread across units rather than charged directly. Non-zero means the split is estimated. */
    residualPremium: number;
    /** True when every dollar was charged directly to a unit, so the numbers are exact. */
    isExact: boolean;
}

export interface MonthlyReportData {
    policyId: string;
    month: number;
    year: number;
    startLayer: PolicyLayer;
    endLayer: PolicyLayer;

    roundingAdjustment: number;

    /** Premium calculated from the composite rates of the coverages */
    subtotalPremium: number;

    /** `subtotalPremium` + any minimium monthly premiums */
    totalPremium: number;

    premiumByCoverage: Map<CoverageOption.Id, MonthlyReportCoveragePremium>;

    /** The whole-policy's minimum premium */
    minimumPremium: number;

    /** The amount of premium, deducted from the total premium due, that covers any proration due to the month including days in which the policy was not active */
    proratedPremiumApplied: number;

    /** The amount of premium, added to the total premium due, which covers any minimum monthly premiums (which could be from either a specific coverage or the policy as a whole) */
    minimumPremiumApplied: number;

    isUnitReporter: boolean;
    isMileageReporter: boolean;
    isRevenueReporter: boolean;
    isPayrollReporter: boolean;
    isAdvancedReporter: boolean;
    isDriverReporter: boolean;

    equipment: {
        tractors: MonthlyReportDataUnit<Tractor>[];
        trailers: MonthlyReportDataUnit<Trailer>[];
        drivers: MonthlyReportDataUnit<Driver>[];

        activeTractors: MonthlyReportDataUnit<Tractor>[];
        activeTrailers: MonthlyReportDataUnit<Trailer>[];
        activeDrivers: MonthlyReportDataUnit<Driver>[];
    };
}

interface Change {
    date: Date;
    unitId: number;
    changesByCoverage: Map<CoverageOption.Id, EquipmentComparison.IChangeMapValue<EquipmentComparison.EquipmentType>>;
    isNewToPolicy: boolean;
    isRemovedFromPolicy: boolean;
}

export interface MonthlyReportChanges {
    tractors: Change[];
    trailers: Change[];
    drivers: Change[];
}

export interface MonthlyReportPayroll {
    policyId: string;
    year: number;
    month: number;
    coverageOptionId: CoverageOption.Id;
    description: string;
    amount: number;
}
